import "./_style.less";
import { useTranslation } from "react-i18next";
import React, { useEffect } from "react";
import { useBookingAssessment } from "../../providers/booking-assessment.provider";
import { useStepTransition } from "../../hooks/use-step-transition";
import {
  FloatingBookingStep,
  FloatingBookingStepItem,
} from "../../../../components";
import BookingAssessmentStepPackage from "../booking-assessment-step-package";
import BookingAssessmentStepPatient from "../booking-assessment-step-patient";
import BookingAssessmentStepPreview from "../booking-assessment-step-preview";
import { message } from "antd";
import { useBookingValidation } from "../../hooks/use-booking-validation";

const BookingAssessmentModule = () => {
  const { t } = useTranslation();
  const {
    onPrevStep,
    onNextStep,
    currentStep,
    form,
    setTriggerValidationError,
    manageStepValidator,
    setStep,
  } = useBookingAssessment();
  const { values, resetForm } = form;
  const { isStepValid, getValidationError } = useBookingValidation(values);

  const previewStep = currentStep === 2;

  const { clearAssessmentData } = useStepTransition({
    values: values as any,
    setFieldValue: form.setFieldValue as any,
    resetForm,
  });

  const handleBack = () => {
    switch (currentStep) {
      case 0:
        resetForm();
        break;
      case 1:
        setTimeout(() => {
          clearAssessmentData();
        }, 0);
        onPrevStep();
        break;
      case 2:
        setTimeout(() => {
          // placeholder for preview-only cleanup
        }, 0);
        onPrevStep();
        break;
      default:
        onPrevStep();
        break;
    }
  };

  const handleNext = () => {
    if (!isStepValid(currentStep)) {
      const errorMessage = getValidationError(currentStep);
      if (errorMessage) {
        message.warning(errorMessage);
      }

      if (currentStep === 1) {
        setTriggerValidationError(true);
        setTimeout(() => {
          setTriggerValidationError(false);
        }, 100);
      }

      return;
    }

    const stepValidatorMessage = manageStepValidator(currentStep) as
      | string
      | null;
    if (stepValidatorMessage) {
      message.warning(stepValidatorMessage);
      return;
    }

    onNextStep();
  };

  useEffect(() => {
    return () => {
      setTimeout(() => {
        if (!localStorage.getItem("redirectToBooking")) {
          resetForm();
          setStep(0);
        }
      }, 500);
    };
  }, []);

  return (
    <div id="booking-assessment-wrapper">
      <div className="container">
        <div className="book-assessment-title">
          {previewStep
            ? t("common.text.previewYourBooking")
            : t("common.text.bookAssessmentAndPrograms")}
        </div>

        <div className="booking-content-padding">
          <BookingAssessmentStepPackage />
          <BookingAssessmentStepPatient />
          <BookingAssessmentStepPreview />
        </div>
      </div>

      <FloatingBookingStep
        previewContent={
          <>
            <FloatingBookingStepItem
              label={t("common.text.package")}
              value={0}
              currentStep={currentStep}
              total={3}
            />
            <FloatingBookingStepItem
              label={
                values.assessment?.has_schedule === true
                  ? t("common.text.schedule")
                  : t("common.text.patient")
              }
              value={1}
              currentStep={currentStep}
              total={3}
            />
            <FloatingBookingStepItem
              label={t("common.text.preview")}
              value={2}
              currentStep={currentStep}
              total={3}
            />
          </>
        }
        onBack={handleBack}
        onNext={handleNext}
      />
    </div>
  );
};

export default BookingAssessmentModule;
